/**
 * Single source of truth for the product form shape.
 * `DEFAULT_PRODUCT_VALUES` is used by Add; `mapProductToFormValues` is
 * used by Update to project a server product record into the same shape.
 *
 * The SEO block is the one exception: it has ten keys across two languages
 * and is defined in `seoFields.js`, which the SEO section of the form also
 * renders from, so the shape and the UI cannot drift apart.
 */
import { emptySeoValues, seoValuesFromProduct } from "./seoFields";

export const DEFAULT_PRODUCT_VALUES = {
  name: "",
  ar_name: "",
  description: "",
  ar_description: "",
  status: 1,
  product_option_type: 1,
  type_id: null,
  cost: "",
  regular_price: "",
  sale_price: "",
  category_ids: [],
  sub_category_ids: [],
  occasion_ids: [],
  brand_ids: [],
  color: null,
  size: null,
  position: null,
  new_from: "",
  new_to: "",
  quantity: "",
  barcode: "",
  has_product_option: 0,
  images: [],
  product_options: [],
  product_related: [],
  addon: [],
  height: null,
  width: null,
  is_b2b: 0,
  seo: emptySeoValues(),
};

const toDateInput = (raw) => {
  if (!raw || raw === "0000-00-00") return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Server occasionally returns reference fields (size, color) as a string id,
// a `{ id, name }` object, or the raw number we sent — coerce them all back
// to a plain numeric id so the select can match an option.
const toIdOrNull = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "object") return toIdOrNull(raw.id);
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const flattenCategoryIds = (raw) => {
  if (!Array.isArray(raw)) return [];
  // Server occasionally wraps in a nested array.
  const flat = raw.flat();
  return flat.map((c) => (typeof c === "object" ? c?.id : c)).filter(Boolean);
};

const mapImages = (raw) => {
  if (!Array.isArray(raw)) return [];
  // `image_url` is the backend's canonical identity for an image row.
  // Dropping it makes the server fall back to an arbitrary variant URL,
  // see the image as "replaced", and purge the real S3 objects.
  return raw.map((img, index) => ({
    id: img.id,
    image_url: img.image_url || img.image_urls?.["800w"] || null,
    image_urls:
      img.image_urls ||
      (img.image_url ? { "800w": img.image_url } : null),
    sort_order:
      typeof img.sort_order === "number" ? img.sort_order : index,
  }));
};

// Project the edited product's linked siblings into react-select option shape
// for display only. The form value itself stays a bare `{ product_option_id }`
// (see below) so the save payload is unaffected; this is purely what hydrates
// the chips on edit. Reads the enriched fields the single-product endpoint
// returns alongside `product_option_id` (name, barcode, image, size, color);
// falls back to `Product #<id>` only when the backend omits the name.
export const mapLinkedProductOptions = (rawOptions) => {
  if (!Array.isArray(rawOptions)) return [];
  return rawOptions
    .map((o) => {
      const id = o?.product_option_id;
      if (id == null) return null;
      return {
        value: id,
        label: o?.name ?? o?.product_name ?? `Product #${id}`,
        data: {
          name: o?.name ?? "",
          ar_name: o?.ar_name ?? "",
          image: o?.image ?? o?.main_image ?? "",
          barcode: o?.barcode ?? "",
          size: o?.size ?? null,
          color: o?.color ?? null,
        },
      };
    })
    .filter(Boolean);
};

export const mapProductToFormValues = (product) => {
  if (!product) return DEFAULT_PRODUCT_VALUES;

  return {
    ...DEFAULT_PRODUCT_VALUES,
    name: product.name ?? "",
    ar_name: product.ar_name ?? "",
    description: product.description ?? "",
    ar_description: product.ar_description ?? "",
    status: product.status ?? 1,
    product_option_type: product.product_option_type ?? 1,
    type_id: product.type?.id ?? null,
    cost: product.cost ?? "",
    regular_price: product.regular_price ?? "",
    sale_price: product.sale_price ?? "",
    category_ids: flattenCategoryIds(product.category_ids),
    sub_category_ids: Array.isArray(product.sub_category_ids)
      ? product.sub_category_ids.map((s) => s.id).filter(Boolean)
      : [],
    occasion_ids: Array.isArray(product.occasion_ids)
      ? product.occasion_ids.map((o) => o.id).filter(Boolean)
      : [],
    brand_ids: Array.isArray(product.brand_ids)
      ? product.brand_ids.map((b) => b.id).filter(Boolean)
      : [],
    color: toIdOrNull(product.color),
    size: toIdOrNull(product.size),
    position: product.position ?? null,
    quantity: product.quantity ?? "",
    new_from: toDateInput(product.new_from),
    new_to: toDateInput(product.new_to),
    barcode: product.barcode ?? "",
    has_product_option: product.has_product_option ?? 0,
    images: mapImages(product.images),
    product_options: Array.isArray(product.product_options)
      ? product.product_options.map((o) => ({
          product_option_id: o.product_option_id,
        }))
      : [],
    product_related: Array.isArray(product.product_related)
      ? product.product_related.map((o) => ({
          product_related_id: o.product_related_id,
        }))
      : [],
    height: product.height ?? null,
    width: product.width ?? null,
    is_b2b: product.is_b2b ?? 0,
    seo: seoValuesFromProduct(product.seo),
  };
};
